import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { message } from "antd";
import { getSafeUrl } from "../utils/helpers.js";
import { useUserState } from "../Contexts/UserContext";

const ACCEPTED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const ACCEPTED_EXT = [".jpg", ".jpeg", ".png", ".webp"];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

function ImagePathGenerator() {
  const navigate = useNavigate();
  const { user } = useUserState();
  const fileInputRef = useRef(null);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [copied, setCopied] = useState(false);

  const commaSeparatedPaths = images
    .filter((img) => img.imageUrl && !img.error)
    .map((img) => img.imageUrl)
    .join(",");

  const goBack = () => {
    navigate(`/user_dash/${user?.currentRole}/my_houses`);
  };

  const validateFile = (file) => {
    const ext = `.${file.name.split(".").pop()?.toLowerCase()}`;
    if (!ACCEPTED_TYPES.includes(file.type) && !ACCEPTED_EXT.includes(ext)) {
      return "Only JPG, PNG, and WEBP files are allowed.";
    }
    if (file.size > MAX_FILE_SIZE) {
      return "File exceeds the 10MB size limit.";
    }
    return null;
  };

  const uploadFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    const pending = [];
    for (const file of files) {
      const error = validateFile(file);
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      pending.push({
        id,
        file,
        name: file.name,
        preview: URL.createObjectURL(file),
        imageUrl: null,
        progress: 0,
        error,
        uploading: !error,
      });
    }

    setImages((prev) => [...prev, ...pending]);
    const toUpload = pending.filter((item) => !item.error);
    if (!toUpload.length) return;

    setUploading(true);
    try {
      await Promise.all(
        toUpload.map(async (item) => {
          const formData = new FormData();
          formData.append("image", item.file);
          try {
            const res = await axios.post(
              `${import.meta.env.VITE_APP_BASE_URL}/api/image/upload`,
              formData,
              {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: (progressEvent) => {
                  const percent = Math.round(
                    (progressEvent.loaded * 100) / (progressEvent.total || 1),
                  );
                  setImages((prev) =>
                    prev.map((img) =>
                      img.id === item.id ? { ...img, progress: percent } : img,
                    ),
                  );
                },
              },
            );
            const imageUrl = res?.data?.data?.imageUrl;
            setImages((prev) =>
              prev.map((img) =>
                img.id === item.id
                  ? {
                      ...img,
                      imageUrl,
                      progress: 100,
                      uploading: false,
                      error: imageUrl ? null : "Upload failed",
                    }
                  : img,
              ),
            );
          } catch (err) {
            console.log("Image path generator upload error", err?.message);
            setImages((prev) =>
              prev.map((img) =>
                img.id === item.id
                  ? {
                      ...img,
                      uploading: false,
                      error: "Upload failed. Please try again.",
                    }
                  : img,
              ),
            );
          }
        }),
      );
    } finally {
      setUploading(false);
    }
  }, []);

  const handleFileChange = (e) => {
    uploadFiles(e.target.files);
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    uploadFiles(e.dataTransfer.files);
  };

  const removeImage = (id) => {
    setImages((prev) => {
      const target = prev.find((img) => img.id === id);
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return prev.filter((img) => img.id !== id);
    });
  };

  const clearAll = () => {
    images.forEach((img) => {
      if (img.preview) URL.revokeObjectURL(img.preview);
    });
    setImages([]);
    setCopied(false);
  };

  const copyPaths = async () => {
    if (!commaSeparatedPaths) {
      message.warning("No image paths to copy yet.");
      return;
    }
    try {
      await navigator.clipboard.writeText(commaSeparatedPaths);
      setCopied(true);
      message.success("Image paths copied to clipboard.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      message.error("Could not copy paths. Please select and copy manually.");
    }
  };

  return (
    <main>
      <div className="container-fluid px-4">
        <div className="row">
          <div className="col-md-12 col-12">
            <div className="main-box img-path-page">
              <div className="content-header">
                <h3>Image Upload & Path Generator</h3>
                <div
                  onClick={goBack}
                  className="back_arrow clickable"
                  title="Click here to go back."
                >
                  <i className="fa fa-solid fa-arrow-left fa-lg"></i>
                </div>
              </div>

              <div className="img-path-body">
                <div className="img-path-info">
                  <i className="fa fa-info-circle"></i>
                  <p>
                    Upload multiple images at once. We will upload them to the
                    server and provide the comma-separated image paths. Copy and
                    paste this path into your CSV file.
                  </p>
                </div>

                <div className="img-path-top-row">
                  <div
                    className={`img-path-dropzone ${isDragging ? "dragging" : ""}`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setIsDragging(true);
                    }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={handleDrop}
                  >
                    <div className="img-path-dropzone-icon">
                      <i className="fa fa-cloud-upload-alt"></i>
                    </div>
                    <p className="img-path-dropzone-text">
                      Drag & drop images here or
                    </p>
                    <button
                      type="button"
                      className="img-path-btn-primary"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                    >
                      Select Images
                    </button>
                    <p className="img-path-dropzone-hint">
                      You can upload JPG, PNG, WEBP files. Max size 10MB per
                      file.
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                      multiple
                      hidden
                      onChange={handleFileChange}
                    />
                  </div>

                  <div className="img-path-guidelines">
                    <h5>Upload Guidelines</h5>
                    <ul>
                      <li>
                        <i className="fa fa-check-circle"></i>
                        Supported formats: JPG, PNG, WEBP
                      </li>
                      <li>
                        <i className="fa fa-check-circle"></i>
                        Max file size: 10MB per image
                      </li>
                      <li>
                        <i className="fa fa-check-circle"></i>
                        You can upload multiple images
                      </li>
                      <li>
                        <i className="fa fa-check-circle"></i>
                        Paths will be generated after upload
                      </li>
                      <li>
                        <i className="fa fa-check-circle"></i>
                        Use these paths in your CSV file
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="img-path-section">
                  <h5>Uploaded Images ({images.length})</h5>
                  {images.length === 0 ? (
                    <div className="img-path-empty">
                      No images selected yet. Select or drag multiple images to
                      generate paths.
                    </div>
                  ) : (
                    <div className="img-path-grid">
                      {images.map((img) => (
                        <div
                          key={img.id}
                          className={`img-path-thumb ${img.error ? "has-error" : ""}`}
                        >
                          <button
                            type="button"
                            className="img-path-remove"
                            onClick={() => removeImage(img.id)}
                            title="Remove"
                          >
                            <i className="fa fa-times"></i>
                          </button>
                          <img
                            src={
                              img.imageUrl
                                ? getSafeUrl(img.imageUrl, img.preview)
                                : img.preview
                            }
                            alt={img.name}
                          />
                          {img.uploading && (
                            <div className="img-path-progress-overlay">
                              <span>{img.progress}%</span>
                            </div>
                          )}
                          {img.error && (
                            <div className="img-path-error-badge">{img.error}</div>
                          )}
                          <p className="img-path-thumb-name" title={img.name}>
                            {img.name}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="img-path-section">
                  <h5>Comma Separated Image Paths</h5>
                  <div className="img-path-output-row">
                    <input
                      type="text"
                      className="img-path-output-input"
                      readOnly
                      value={commaSeparatedPaths}
                      placeholder="Uploaded image paths will appear here..."
                    />
                    <button
                      type="button"
                      className="img-path-btn-primary img-path-copy-btn"
                      onClick={copyPaths}
                      disabled={!commaSeparatedPaths}
                    >
                      <i className={`fa ${copied ? "fa-check" : "fa-copy"}`}></i>
                      {copied ? "Copied" : "Copy Paths"}
                    </button>
                  </div>
                  <button
                    type="button"
                    className="img-path-clear-btn"
                    onClick={clearAll}
                    disabled={!images.length}
                  >
                    <i className="fa fa-trash"></i>
                    Clear All
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

export default ImagePathGenerator;
